// src/app/shared/utils/data-flattener.ts

import { EntityLookupModel } from "@models/entity-lookup.model";
import { SearchGlobalModel } from "@models/search-global.model";
import { WallUtilsHelper } from "./utils/wall-utils.helper";
import { PersonWithDetails } from "@models/people.model";
import { DataFlattenerHomeModel } from "@models/data-flattener-home.model";
import { DataFlattenerLocationModel } from "@models/data-flattener-location.model";
import { DataFlattenerFloorModel } from "@models/data-flattener-floor.model";
import { ZendeskArticleModel } from "@models/zendesk-article.model";

const searchableLocations: SearchGlobalModel.SearchLocationDocument[] = [];
const searchableSubLocations: SearchGlobalModel.SearchSubLocationDocument[] = [];
const searchableEdges: SearchGlobalModel.SearchEdgeDocument[] = [];
const searchableCameras: SearchGlobalModel.SearchCameraDocument[] = [];
const searchableEvents: SearchGlobalModel.SearchEventDocument[] = [];
const searchableWalls: SearchGlobalModel.SearchWallDocument[] = [];
const searchableAlarms: SearchGlobalModel.SearchAlarmDocument[] = [];
const searchablePages = [];
const searchableSpeakers: SearchGlobalModel.SearchSpeakerDocument[] = [];
const searchablePeople: SearchGlobalModel.SearchPeoplenDocument[] = [];
const searchableFloors: SearchGlobalModel.SearchFloorDocument[] = [];
const searchableBuildings: SearchGlobalModel.SearchBuildingDocument[] = [];

export function buildNameLookups(flatLocations: any[]) {
    const cameraIdToName: Record<string, string> = {};
    const edgeIdToName: Record<string, string> = {};
    const locationIdToName: Record<string, string> = {};

    flatLocations.forEach(item => {
        if (item.type === 'location') {
            locationIdToName[item.locationId] = item.name;
        } else if (item.type === 'edge') {
            edgeIdToName[item.edgeId] = item.name;
        } else if (item.type === 'camera') {
            cameraIdToName[item.cameraId] = item.name;
        }
    });

    return { cameraIdToName, edgeIdToName, locationIdToName };
}

export function getCameraLocationPath(struct: any[], cameraId: string): EntityLookupModel.SubLocationPath.CameraLocationPath | null {
    const locationMap = new Map();
    struct.forEach(loc => locationMap.set(loc._id, loc));

    let cameraLocation = null;
    let foundEdgeId = null;

    for (const location of struct) {
        const cam = location.cameras.find(c => c.cameraId === cameraId);
        if (cam) {
            cameraLocation = location;
            foundEdgeId = cam.edgeId;
            break;
        }
    }

    if (!cameraLocation) return null;

    const locations = [];
    let currentLoc = cameraLocation;

    while (currentLoc) {
        const parent = currentLoc.parent ? locationMap.get(currentLoc.parent) : null;
        locations.push({
            _id: currentLoc._id,
            name: currentLoc.name,
            type: currentLoc.parent ? "sub" : "location",
            parentId: parent ? parent._id : null,
            parentName: parent ? parent.name : null,
            edgeId: foundEdgeId
        });
        currentLoc = parent;
    }

    const subLocationTree = locations.map(loc => loc.name).join(' ');

    return {
        cameraId,
        edgeId: foundEdgeId,
        locations,
        subLocationTree,
        subLocationNames: cameraLocation.name
    };
}

export function getAllSubLocations(struct: DataFlattenerHomeModel.Location[], parentId: string): EntityLookupModel.SubLocationPath.SubLocations {
    const locationMap = new Map<string, DataFlattenerHomeModel.Location>();
    struct.forEach(loc => locationMap.set(loc._id, loc));

    const result = [];
    let subLocationNamesAccumulator = "";

    function recurse(currentParentId) {
        const children = struct.filter(loc => loc.parent === currentParentId);
        for (const child of children) {
            const parent = locationMap.get(child.parent);
            const subLoc = {
                _id: child._id,
                name: child.name,
                type: !!parent?._id ? "sub" : "location",
                parentId: parent ? parent._id : null,
                parentName: parent ? parent.name : null
            };
            result.push(subLoc);
            subLocationNamesAccumulator += child.name + " ";
            recurse(child._id);
        }
    }

    recurse(parentId);

    return {
        subLocations: result,
        subLocationNames: subLocationNamesAccumulator.trim()
    };
}


export function buildStructuredSubLocationsFromCameraPath(cameraPath: EntityLookupModel.SubLocationPath.CameraLocationPath): { subLocationId: string; subLocationName: string; }[] {
    if (!cameraPath?.locations || !Array.isArray(cameraPath.locations)) {
        return [];
    }

    return cameraPath.locations
        .filter(loc => loc.type === "sub")
        .map(subLoc => ({
            subLocationId: subLoc._id,
            subLocationName: subLoc.name
        }));
}

export function buildStructuredSubLocationsFromEdgePaths(edgePaths: EntityLookupModel.SubLocationPath.EdgeLocationPaths): { subLocationId: string; subLocationName: string; }[] {
    if (!edgePaths?.locations || !Array.isArray(edgePaths.locations)) {
        return [];
    }

    return edgePaths.locations
        .filter(loc => loc.type === "sub")
        .map(subLoc => ({
            subLocationId: subLoc._id,
            subLocationName: subLoc.name
        }));
}




export function getAllRelatedLocationsForEdgeId(struct: any, edgeId: string): EntityLookupModel.SubLocationPath.EdgeLocationPaths {
    const locationMap = new Map();
    struct.forEach(loc => locationMap.set(loc._id, loc));

    const uniqueLocationIds = new Set();

    function addParents(location) {
        if (!location) return;
        if (!uniqueLocationIds.has(location._id)) {
            uniqueLocationIds.add(location._id);
            if (location.parent) addParents(locationMap.get(location.parent));
        }
    }

    function addDescendants(parentId) {
        const children = struct.filter(loc => loc.parent === parentId);
        for (const child of children) {
            if (!uniqueLocationIds.has(child._id)) {
                uniqueLocationIds.add(child._id);
                addDescendants(child._id);
            }
        }
    }

    const rootLocations = struct.filter(location =>
        location.cameras.some(cam => cam.edgeId === edgeId)
    );

    for (const loc of rootLocations) {
        addParents(loc);
        addDescendants(loc._id);
    }

    let locationNamesAccumulator = "";

    const result = Array.from(uniqueLocationIds).map(id => {
        const loc = locationMap.get(id);
        const parent = loc.parent ? locationMap.get(loc.parent) : null;

        locationNamesAccumulator += loc.name + " ";

        return {
            _id: loc._id,
            name: loc.name,
            type: loc.parent ? ("sub" as const) : ("location" as const),
            parentId: parent ? parent._id : null,
            parentName: parent ? parent.name : null
        };
    });

    return {
        edgeId,
        locations: result,
        locationNames: locationNamesAccumulator.trim()
    };
}

export function flattenHomeData(
    home: DataFlattenerHomeModel.Location[],
    searchableLocationMap: Map<string, SearchGlobalModel.SearchLocationDocument>,
    uniqueLocationCameras: Map<string, {
        cameraId: string;
        edgeId: string;
        locationId: string;
        cameraName: string;
        edgeName: string;
        locationName: string;
    }>
): {
    searchableSubLocations: SearchGlobalModel.SearchSubLocationDocument[]
} {

    home.forEach(subLocation => {

        const location = searchableLocationMap.get(subLocation._id);

        // Only process sub-locations that have this location as parent and aren't already stored as locations
        // if (subLocation.parent === location._id && !locationIds.has(subLocation._id)) {
        if (!location?._id) {

            // Get all sub-locations in the hierarchy starting from this sub-location
            const allSubLocationsInHierarchy = getAllSubLocations(home, subLocation._id);

            const cameras: {
                cameraId: string;
                edgeId: string;
                locationId: string;
                cameraName: string;
                edgeName: string;
                locationName: string;
            }[] = []

            // Collect cameras that belong to this sub-location from the home document
            if (subLocation.cameras && Array.isArray(subLocation.cameras)) {
                subLocation.cameras.forEach((subLocationCamera) => {
                    const key = `${subLocationCamera.cameraId}-${subLocationCamera.edgeId}-${subLocationCamera.locationId}`;
                    const uniqueCamera = uniqueLocationCameras.get(key);
                    if (uniqueCamera && uniqueCamera.cameraId) {
                        cameras.push(uniqueCamera)
                    }
                });
            }

            // Add the immediate sub-location
            // Group cameras by edge to create edge objects
            const edgeMap = new Map<string, {
                edgeId: string;
                edgeName: string;
                locationName: string;
                cameraCount: number;
            }>();

            cameras.forEach((camera) => {
                const edgeId = camera.edgeId;
                if (!edgeMap.has(edgeId)) {
                    edgeMap.set(edgeId, {
                        edgeId: edgeId,
                        edgeName: camera.edgeName || 'Unnamed Edge',
                        locationName: camera.locationName || '',
                        cameraCount: 0
                    });
                }
                edgeMap.get(edgeId)!.cameraCount++;
            });

            const uniqueLocations = new Map<string, { locationId: string, locationName: string }>();

            cameras.forEach(camera => {
                if (!uniqueLocations.has(camera.locationId)) {
                    uniqueLocations.set(camera.locationId, {
                        locationId: camera.locationId,
                        locationName: camera.locationName
                    });
                }
            });

            const subLocationDoc: SearchGlobalModel.SearchSubLocationDocument = {
                type: 'sublocation',
                name: subLocation.name,
                subInfo: subLocation.address || '',
                icon: 'fas fa-building',
                _id: subLocation._id,
                subLocationId: subLocation._id,
                parentLocationId: subLocation.parent,
                raw: {
                    address: subLocation.address || '',
                    cameras,
                    locations: Array.from(uniqueLocations.values()),
                    edges: Array.from(edgeMap.values()).map(edge => ({
                        edgeId: edge.edgeId,
                        edgeName: edge.edgeName,
                        cameraCount: edge.cameraCount
                    })),
                    subLocations: allSubLocationsInHierarchy.subLocations.map(subLoc => ({
                        subLocationId: subLoc._id,
                        subLocationName: subLoc.name
                    }))
                }
            };

            searchableSubLocations.push(subLocationDoc);

        }
    });


    return {
        searchableSubLocations,
    };
}

export function flattenLocationData(locations: DataFlattenerLocationModel.Location[], home: DataFlattenerHomeModel.Location[]): {
    searchableLocations: SearchGlobalModel.SearchLocationDocument[],
    searchableSubLocations: SearchGlobalModel.SearchSubLocationDocument[],
    searchableEdges: SearchGlobalModel.SearchEdgeDocument[],
    searchableCameras: SearchGlobalModel.SearchCameraDocument[],
    searchableSpeakers: SearchGlobalModel.SearchSpeakerDocument[],
} {
    const searchableLocationsMap = new Map<string, SearchGlobalModel.SearchLocationDocument>();

    const locationLookup = new Map<string, string>();
    locations.forEach(location => {
        locationLookup.set(location._id, location.name);
    });

    const uniqueOrgCameras = new Map<string, {
        cameraId: string;
        edgeId: string;
        locationId: string;
        cameraName: string;
        edgeName: string;
        locationName: string;
    }>();

    locations.forEach(location => {

        const locationSubLocationPath = getAllSubLocations(home, location._id);

        const locationEdgesMap = new Map<string, { edgeId: string, edgeName: string, cameraCount: number }>();

        const searchableLocationItem: SearchGlobalModel.SearchLocationDocument = {
            type: 'location',
            name: location.name,
            subInfo: location.city || '',
            icon: 'fas fa-building',
            _id: location._id,
            locationId: location._id,
            raw: {
                address: location.address || '',
                city: location.city || '',
                state: location.state || '',
                zip: location.zip || '',
                cameras: [],
                edges: [],
                subLocations: locationSubLocationPath.subLocations.map(subLoc => ({
                    subLocationId: subLoc._id,
                    subLocationName: subLoc.name
                }))
            }
        }

        if (location.edges && typeof location.edges === 'object') {
            Object.values(location.edges).forEach((edge) => {

                if (!locationEdgesMap.has(edge.edgeId)) {
                    locationEdgesMap.set(edge.edgeId, {
                        edgeId: edge.edgeId,
                        edgeName: edge.name || '',
                        cameraCount: 0
                    });
                }

                const edgeSubLocationPath = getAllRelatedLocationsForEdgeId(home, edge.edgeId);

                const uniqueEdgeCameras = new Map<string, {
                    cameraId: string;
                    edgeId: string;
                    locationId: string;
                    cameraName: string;
                    edgeName: string;
                    locationName: string;
                }>();


                if (edge.cameras && typeof edge.cameras === 'object') {
                    Object.values(edge.cameras).forEach((camera) => {
                        const cameraId = camera?.edgeOnly?.cameraId;
                        if (!cameraId) {
                            return;
                        }

                        const cameraSubLocationPath = getCameraLocationPath(home, cameraId);

                        const key = `${cameraId}-${edge.edgeId}-${location._id}`;
                        if (!uniqueOrgCameras.has(key)) {
                            uniqueOrgCameras.set(key, {
                                cameraId,
                                edgeId: edge.edgeId,
                                locationId: location._id,
                                cameraName: camera.edgeOnly?.name || '',
                                edgeName: edge.name || '',
                                locationName: location.name || ''
                            });
                        }

                        if (!uniqueEdgeCameras.has(key)) {
                            uniqueEdgeCameras.set(key, {
                                cameraId,
                                edgeId: edge.edgeId,
                                locationId: location._id,
                                cameraName: camera.edgeOnly?.name || '',
                                edgeName: edge.name || '',
                                locationName: location.name || ''
                            });

                            const edgeItem = locationEdgesMap.get(edge.edgeId);
                            if (edgeItem) {
                                edgeItem.cameraCount++;
                            }
                        }

                        searchableCameras.push({
                            type: 'camera',
                            name: camera.edgeOnly?.name || '',
                            subInfo: camera.edgeOnly?.description || '',
                            icon: 'fas fa-video',
                            _id: cameraId,
                            locationId: location._id,
                            edgeId: edge.edgeId,
                            cameraId,
                            locationName: location.name || '',
                            edgeName: edge.name || '',
                            raw: {
                                ipAddress: camera.edgeOnly?.networkDetails?.ipAddress,
                                address: camera.cloudOnly?.address || '',
                                description: camera.edgeOnly?.description || '',
                                retentionDays: camera.edgeOnly?.retentionDays?.toString() || '',
                                subLocations: buildStructuredSubLocationsFromCameraPath(cameraSubLocationPath)
                            }
                        });
                    });
                }

                const edgeCameras = Array.from(uniqueEdgeCameras.values());

                searchableEdges.push({
                    type: 'core',
                    name: edge.name,
                    subInfo: location.address || '',
                    icon: 'fas fa-server',
                    _id: edge.edgeId,
                    locationId: location._id,
                    locationName: location.name,
                    edgeId: edge.edgeId,
                    raw: {
                        address: location.address || '',
                        cameras: edgeCameras,
                        subLocations: buildStructuredSubLocationsFromEdgePaths(edgeSubLocationPath)
                    }
                });

                searchableLocationItem.raw.cameras.push(...edgeCameras);

            });
        }

        searchableLocationItem.raw.edges = Array.from(locationEdgesMap.values());

        searchableLocationsMap.set(location._id, searchableLocationItem);
        searchableLocations.push(searchableLocationItem);
    });

    locations.forEach(location => {
        if (location.devices && typeof location.devices === 'object') {
            Object.values(location.devices).forEach((device) => {
                if (device.type === 0) {

                    const speakerCameras: {
                        cameraId: string;
                        edgeId: string;
                        locationId: string;
                        cameraName: string;
                        edgeName: string;
                        locationName: string;
                    }[] = [];

                    if (device.cameras && typeof device.cameras === 'object') {
                        Object.values(device.cameras).forEach((speakerCamera) => {
                            const key = `${speakerCamera.cameraId}-${speakerCamera.edgeId}-${speakerCamera.locationId}`;


                            const cameraInfo = uniqueOrgCameras.get(key);
                            if (cameraInfo) {
                                speakerCameras.push(cameraInfo);
                            }
                        });
                    }

                    const seenLocationIds = new Set<string>();
                    const speakerLocations: { locationId: string, locationName: string }[] = [];

                    const speakerEdgeMap = new Map<string, { edgeId: string, edgeName: string, cameraCount: number }>();

                    speakerCameras.forEach(camera => {
                        if (!seenLocationIds.has(camera.locationId)) {
                            seenLocationIds.add(camera.locationId);
                            speakerLocations.push({
                                locationId: camera.locationId,
                                locationName: camera.locationName
                            });
                        }

                        if (!speakerEdgeMap.has(camera.edgeId)) {
                            speakerEdgeMap.set(camera.edgeId, {
                                edgeId: camera.edgeId,
                                edgeName: camera.edgeName,
                                cameraCount: 1
                            });
                        } else {
                            speakerEdgeMap.get(camera.edgeId)!.cameraCount++;
                        }
                    });

                    const speakerEdges = Array.from(speakerEdgeMap.values());

                    const speakerDoc: SearchGlobalModel.SearchSpeakerDocument = {
                        type: 'speaker' as const,
                        name: device.name,
                        subInfo: device.sipUrl || '',
                        icon: 'fas fa-volume-up',
                        _id: device._id,
                        speakerId: device._id,
                        locationId: location._id,
                        raw: {
                            sipUrl: device.sipUrl || '',
                            model: device.model || '',
                            cameras: speakerCameras,
                            locations: speakerLocations,
                            edges: speakerEdges
                        }
                    };

                    searchableSpeakers.push(speakerDoc);

                    const searchableLocationItem = searchableLocationsMap.get(location._id);
                    if (searchableLocationItem) {
                        if (!searchableLocationItem.raw.speakers) {
                            searchableLocationItem.raw.speakers = [];
                        }
                        searchableLocationItem.raw.speakers.push({
                            speakerId: device._id,
                            speakerName: device.name || 'Unnamed Speaker'
                        });
                    }
                }
            });
        }
    });

    flattenHomeData(home, searchableLocationsMap, uniqueOrgCameras);

    return {
        searchableLocations,
        searchableSubLocations,
        searchableEdges,
        searchableCameras,
        searchableSpeakers
    };
}

export function flattenAlarmData(alarms: any[], events: SearchGlobalModel.SearchEventDocument[]): {
    searchableAlarms: SearchGlobalModel.SearchAlarmDocument[]
} {
    alarms.forEach((alarm: any) => {

        let cameras = [];
        let alarmEvents = [];

        if (Array.isArray(alarm?.alertIds) && alarm.alertIds.length > 0) {

            alarm.alertIds.forEach((alertId: string) => {
                const event = events.find(e => e.eventId === alertId);
                if (event && event.raw && Array.isArray(event.raw.cameras)) {
                    cameras = cameras.concat(event.raw.cameras);
                }
                if (event) {
                    alarmEvents.push({
                        eventId: event.eventId,
                        eventName: event.name,
                        sentence: event.sentence || '',
                        sentenceCameraInfo: event.sentenceCameraInfo || null
                    });
                }
            })

        }

        searchableAlarms.push({
            type: 'alarm',
            name: alarm.name || '',
            subInfo: alarm.description || '',
            icon: 'fas fa-bell',
            _id: alarm._id,
            alarmId: alarm._id,
            state: alarm.state,
            reArmMin: alarm.reArmMin,
            disArmMin: alarm.disArmMin,
            scheduleId: alarm.scheduleId,
            stateUpdatedAt: alarm.stateUpdatedAt,
            calculatedState: alarm.calculatedState,
            notification: alarm.notification,
            notificationScheduleId: alarm.notification?.scheduleId || '',
            raw: {
                cameras,
                events: alarmEvents
            }
        });
    });

    return {
        searchableAlarms
    };
}


export function flattenEventData(events: any[], lookups: EntityLookupModel.EntityLookupMap): {
    searchableEvents: SearchGlobalModel.SearchEventDocument[]
} {

    events.forEach((event: any) => {

        const uniqueCameras = new Map<string, {
            cameraId: string,
            edgeId: string,
            locationId: string,
            cameraName: string,
            edgeName: string,
            locationName: string
        }>();
        const uniqueEdges = new Map<string, {
            edgeId: string,
            edgeName: string,
            cameraCount: number
        }>();

        const eventCameras: {
            cameraId: string,
            edgeId: string,
            locationId: string
        }[] = event.selectedFlow?.formValue?.['camera'];


        eventCameras?.forEach(c => {
            const key = `${c.cameraId}-${c.edgeId}-${c.locationId}`;
            if (!uniqueCameras.has(key)) {
                uniqueCameras.set(key, {
                    cameraId: c.cameraId,
                    edgeId: c.edgeId,
                    locationId: c.locationId,
                    cameraName: lookups?.[c.cameraId]?.name || '',
                    edgeName: lookups?.[c.edgeId]?.name || '',
                    locationName: lookups?.[c.locationId]?.name || ''
                });
            }

            if (c.edgeId) {
                if (uniqueEdges.has(c.edgeId)) {
                    const edge = uniqueEdges.get(c.edgeId)!;
                    edge.cameraCount++;
                } else {
                    uniqueEdges.set(c.edgeId, {
                        edgeId: c.edgeId,
                        edgeName: lookups?.[c.edgeId]?.name || '',
                        cameraCount: 1
                    });
                }
            }
        });

        const uniqueEventLocations = new Map<string, { locationId: string, locationName: string }>();
        Array.from(uniqueCameras.values()).forEach(camera => {
            if (!uniqueEventLocations.has(camera.locationId)) {
                uniqueEventLocations.set(camera.locationId, {
                    locationId: camera.locationId,
                    locationName: camera.locationName
                });
            }
        });

        searchableEvents.push({
            type: 'event',
            name: event.name || '',
            subInfo: event.description || '',
            icon: 'fas fa-bolt',
            _id: event._id,
            eventId: event._id,
            sentence: event.sentence || '',
            sentenceCameraInfo: event.sentenceCameraInfo || null,
            enabled: event.enabled,
            raw: {
                cameras: Array.from(uniqueCameras.values()),
                locations: Array.from(uniqueEventLocations.values()),
                edges: Array.from(uniqueEdges.values())
            }
        });
    });

    return {
        searchableEvents
    };

}

export function extractWallCameras(wall: any, lookups?: EntityLookupModel.EntityLookupMap): {
    cameras: any[];
    eventIds: string[];
    hasAllEvents: boolean;
    uniqueCameras: Map<string, {
        cameraId: string,
        edgeId: string,
        locationId: string,
        cameraName: string,
        edgeName: string,
        locationName: string
    }>;
    uniqueEdges: Map<string, {
        edgeId: string,
        edgeName: string,
        cameraCount: number
    }>;
} {
    if (!wall.sets?.length) return {
        cameras: [],
        eventIds: [],
        hasAllEvents: false,
        uniqueCameras: new Map(),
        uniqueEdges: new Map()
    };

    const cameras: any[] = [];
    const eventIds: string[] = [];
    const seenCameras = new Set<string>();
    const seenEvents = new Set<string>();
    let hasAllEvents = false;
    const uniqueCameras = new Map<string, {
        cameraId: string,
        edgeId: string,
        locationId: string,
        cameraName: string,
        edgeName: string,
        locationName: string
    }>();
    const uniqueEdges = new Map<string, {
        edgeId: string,
        edgeName: string,
        cameraCount: number
    }>();

    for (const set of wall.sets) {
        if (!set.tiles?.length) continue;

        for (const tile of set.tiles) {
            const camera = tile.camera;
            if (camera && !seenCameras.has(camera.cameraId)) {
                seenCameras.add(camera.cameraId);
                cameras.push(camera);

                if (lookups) {
                    const key = `${camera.cameraId}-${camera.edgeId}-${camera.locationId}`;
                    if (!uniqueCameras.has(key)) {
                        uniqueCameras.set(key, {
                            cameraId: camera.cameraId,
                            edgeId: camera.edgeId,
                            locationId: camera.locationId,
                            cameraName: lookups?.[camera.cameraId]?.name || '',
                            edgeName: lookups?.[camera.edgeId]?.name || '',
                            locationName: lookups?.[camera.locationId]?.name || ''
                        });
                    }

                    if (camera.edgeId) {
                        if (uniqueEdges.has(camera.edgeId)) {
                            const edge = uniqueEdges.get(camera.edgeId)!;
                            edge.cameraCount++;
                        } else {
                            uniqueEdges.set(camera.edgeId, {
                                edgeId: camera.edgeId,
                                edgeName: lookups?.[camera.edgeId]?.name || '',
                                cameraCount: 1
                            });
                        }
                    }
                }
            }

            if (tile.allEvents === true) {
                hasAllEvents = true;
                continue;
            }

            if (tile.events && Array.isArray(tile.events) && tile.events.length > 0) {
                tile.events.forEach((eventObj: { eventId: string }) => {
                    if (!seenEvents.has(eventObj.eventId)) {
                        seenEvents.add(eventObj.eventId);
                        eventIds.push(eventObj.eventId);
                    }
                });
            }
        }
    }

    return { cameras, eventIds, hasAllEvents, uniqueCameras, uniqueEdges };
}

export function flattenWallData(
    walls: any[],
    lookups: EntityLookupModel.EntityLookupMap,
    events: SearchGlobalModel.SearchEventDocument[]
): {
    searchableWalls: SearchGlobalModel.SearchWallDocument[]
} {
    const searchableWalls: SearchGlobalModel.SearchWallDocument[] = [];

    walls.forEach((wall: any) => {

        const { cameras: wallCameras, eventIds, hasAllEvents, uniqueCameras, uniqueEdges } = extractWallCameras(wall, lookups);

        let wallEvents: any[] = [];

        if (hasAllEvents) {
            wallEvents = events.map(event => ({
                eventId: event.eventId,
                eventName: event.name,
                sentence: event.sentence || '',
                sentenceCameraInfo: event.sentenceCameraInfo || null
            }));
        } else {
            wallEvents = eventIds.map(eventId => {
                const event = events.find(e => e.eventId === eventId);
                if (event) {
                    return {
                        eventId: event.eventId,
                        eventName: event.name,
                        sentence: event.sentence || '',
                        sentenceCameraInfo: event.sentenceCameraInfo || null
                    };
                }
                return null;
            }).filter(Boolean);
        }

        const setCount = wall.sets?.length || 0;
        const cameraCount = uniqueCameras.size;

        const uniqueWallLocations = new Map<string, { locationId: string, locationName: string }>();
        Array.from(uniqueCameras.values()).forEach(camera => {
            if (!uniqueWallLocations.has(camera.locationId)) {
                uniqueWallLocations.set(camera.locationId, {
                    locationId: camera.locationId,
                    locationName: camera.locationName
                });
            }
        });

        const isMobileSupported = WallUtilsHelper.isWallMobileSupported(wall);
        const isAlertWall = WallUtilsHelper.isWallAlertWall(wall);
        const hasYoutube = WallUtilsHelper.isWallContainsYoutube(wall);

        searchableWalls.push({
            type: 'wall',
            name: wall.name,
            subInfo: wall.description || '',
            icon: 'fas fa-th-large',
            _id: wall._id,
            wallId: wall._id,
            raw: {
                cameras: Array.from(uniqueCameras.values()),
                locations: Array.from(uniqueWallLocations.values()),
                edges: Array.from(uniqueEdges.values()),
                events: wallEvents,
                hasAllEvents,
                isPrivate: wall.settings?.isPrivate || false,
                setCount,
                cameraCount,
                isMobileSupported,
                isAlertWall,
                hasYoutube
            }
        });
    });

    return {
        searchableWalls
    };
}

export function flattenPeopleData(people: PersonWithDetails[], lookups: EntityLookupModel.EntityLookupMap): {
    searchablePeople: SearchGlobalModel.SearchPeoplenDocument[]
} {
    const searchablePeopleLocal: SearchGlobalModel.SearchPeoplenDocument[] = [];

    const uniqueCameras = new Map<string, {
        cameraId: string,
        edgeId: string,
        locationId: string,
        cameraName: string,
        edgeName: string,
        locationName: string
    }>();

    people.forEach((person) => {
        const hasLocationData = person.cameraId && person.edgeId && person.locationId;
        let camera: any = null;

        if (hasLocationData) {
            const key = `${person.cameraId}-${person.edgeId}-${person.locationId}`;

            if (!uniqueCameras.has(key)) {
                uniqueCameras.set(key, {
                    cameraId: person.cameraId,
                    edgeId: person.edgeId,
                    locationId: person.locationId,
                    cameraName: lookups?.[person.cameraId]?.name || '',
                    edgeName: lookups?.[person.edgeId]?.name || '',
                    locationName: lookups?.[person.locationId]?.name || ''
                });
            }
            camera = uniqueCameras.get(key);
        }

        searchablePeopleLocal.push({
            type: 'person',
            name: person.name || 'Unnamed Person',
            subInfo: person.appearance && person.appearance > 0 ? `${person.appearance} appearances` : 'No recognitions',
            icon: 'ui-icon-person',
            _id: (person.id || person.personId).toString(),
            personId: person.personId.toString(),
            appearance: person.appearance || 0,
            bucketId: person.bucketId,
            lastSeen: person.lastSeen,
            timestamp: person.timestamp,
            isHidden: person.isHidden || false,
            bestImage: person.bestImage || '',
            raw: {
                cameras: camera ? [camera] : [],
                locations: camera ? [{
                    locationId: camera.locationId,
                    locationName: camera.locationName
                }] : [],
                edges: camera ? [{
                    edgeId: camera.edgeId,
                    edgeName: camera.edgeName,
                    cameraCount: 1
                } as any] : []
            }
        });
    });

    return {
        searchablePeople: searchablePeopleLocal
    };
}

export function flattenFloorData(buildings: DataFlattenerFloorModel.Building[], lookups: EntityLookupModel.EntityLookupMap): {
    searchableBuildings: SearchGlobalModel.SearchBuildingDocument[],
    searchableFloors: SearchGlobalModel.SearchFloorDocument[],
    cameraUpdates: { id: string, changes: { floors: any[], buildings: any[] } }[]
} {
    const searchableBuildingsLocal: SearchGlobalModel.SearchBuildingDocument[] = [];
    const searchableFloorsLocal: SearchGlobalModel.SearchFloorDocument[] = [];
    const cameraUpdatesMap = new Map<string, { floors: Set<any>, buildings: Set<any> }>();

    buildings.forEach((building) => {
        const allBuildingCameras = new Map<string, {
            cameraId: string,
            edgeId: string,
            locationId: string,
            cameraName: string,
            edgeName: string,
            locationName: string
        }>();

        const buildingEdges = new Map<string, {
            edgeId: string,
            edgeName: string,
            cameraCount: number
        }>();

        const buildingLocations = new Map<string, {
            locationId: string,
            locationName: string
        }>();

        building.floors?.forEach((floor) => {
            floor.cameras?.forEach((floorCamera) => {
                const key = `${floorCamera.cameraId}-${floorCamera.edgeId}-${floorCamera.locationId}`;

                if (!allBuildingCameras.has(key)) {
                    allBuildingCameras.set(key, {
                        cameraId: floorCamera.cameraId,
                        edgeId: floorCamera.edgeId,
                        locationId: floorCamera.locationId,
                        cameraName: lookups?.[floorCamera.cameraId]?.name || '',
                        edgeName: lookups?.[floorCamera.edgeId]?.name || '',
                        locationName: lookups?.[floorCamera.locationId]?.name || ''
                    });
                }

                if (!buildingEdges.has(floorCamera.edgeId)) {
                    buildingEdges.set(floorCamera.edgeId, {
                        edgeId: floorCamera.edgeId,
                        edgeName: lookups?.[floorCamera.edgeId]?.name || '',
                        cameraCount: 0
                    });
                }
                buildingEdges.get(floorCamera.edgeId)!.cameraCount++;

                if (!buildingLocations.has(floorCamera.locationId)) {
                    buildingLocations.set(floorCamera.locationId, {
                        locationId: floorCamera.locationId,
                        locationName: lookups?.[floorCamera.locationId]?.name || ''
                    });
                }
            });

            const floorCameras: {
                cameraId: string,
                edgeId: string,
                locationId: string,
                cameraName: string,
                edgeName: string,
                locationName: string,
                position: { x: number, y: number },
                angle: number,
                radius: number,
                rotation: number
            }[] = [];

            const floorEdges = new Map<string, {
                edgeId: string,
                edgeName: string,
                cameraCount: number
            }>();

            const floorLocations = new Map<string, {
                locationId: string,
                locationName: string
            }>();

            floor.cameras?.forEach((floorCamera) => {
                const cameraInfo = allBuildingCameras.get(`${floorCamera.cameraId}-${floorCamera.edgeId}-${floorCamera.locationId}`);
                if (cameraInfo) {
                    floorCameras.push({
                        ...cameraInfo,
                        position: floorCamera.position,
                        angle: floorCamera.angle,
                        radius: floorCamera.radius,
                        rotation: floorCamera.rotation
                    });

                    const cameraKey = floorCamera.cameraId;
                    if (!cameraUpdatesMap.has(cameraKey)) {
                        cameraUpdatesMap.set(cameraKey, {
                            floors: new Set(),
                            buildings: new Set()
                        });
                    }

                    const cameraUpdate = cameraUpdatesMap.get(cameraKey)!;
                    cameraUpdate.floors.add(JSON.stringify({ floorId: floor.id, floorName: floor.name }));
                    cameraUpdate.buildings.add(JSON.stringify({ buildingId: building._id, buildingName: building.name }));


                    if (!floorEdges.has(floorCamera.edgeId)) {
                        floorEdges.set(floorCamera.edgeId, {
                            edgeId: floorCamera.edgeId,
                            edgeName: lookups?.[floorCamera.edgeId]?.name || '',
                            cameraCount: 0
                        });
                    }
                    floorEdges.get(floorCamera.edgeId)!.cameraCount++;

                    if (!floorLocations.has(floorCamera.locationId)) {
                        floorLocations.set(floorCamera.locationId, {
                            locationId: floorCamera.locationId,
                            locationName: lookups?.[floorCamera.locationId]?.name || ''
                        });
                    }
                }
            });

            searchableFloorsLocal.push({
                type: 'floor',
                name: floor.name,
                subInfo: `Floor ${floor.number}`,
                icon: 'fas fa-map',
                _id: floor.id,
                buildingId: building._id,
                floorId: floor.id,
                floorImage: floor.floorImage,
                buildingName: building.name,
                raw: {
                    cameras: floorCameras,
                    locations: Array.from(floorLocations.values()),
                    edges: Array.from(floorEdges.values())
                }
            });
        });

        searchableBuildingsLocal.push({
            type: 'building',
            name: building.name,
            subInfo: `${building.floors?.length || 0} ${(building.floors?.length || 0) === 1 ? 'floor' : 'floors'}`,
            icon: 'fas fa-building',
            _id: building._id,
            buildingId: building._id,
            createdAt: building.createdAt,
            updatedAt: building.updatedAt,
            raw: {
                cameras: Array.from(allBuildingCameras.values()),
                locations: Array.from(buildingLocations.values()),
                edges: Array.from(buildingEdges.values()),
                floors: building.floors?.map(floor => ({
                    floorId: floor.id,
                    floorName: floor.name
                })) || []
            }
        });
    });

    const cameraUpdates = Array.from(cameraUpdatesMap.entries()).map(([cameraId, data]) => ({
        id: cameraId,
        changes: {
            floors: Array.from(data.floors).map(f => JSON.parse(f)),
            buildings: Array.from(data.buildings).map(b => JSON.parse(b))
        }
    }));

    return {
        searchableBuildings: searchableBuildingsLocal,
        searchableFloors: searchableFloorsLocal,
        cameraUpdates
    };
}

export function flattenArticleData(articles: ZendeskArticleModel.ZendeskArticleRaw[]): {
    searchableArticles: SearchGlobalModel.SearchArticleDocument[]
} {

    const searchableArticles: SearchGlobalModel.SearchArticleDocument[] = [];

    articles.forEach((article) => {


        const category = (article.label_names && article.label_names.length)
            ? article.label_names.join(', ')
            : undefined;

        const rawBodyHtml = article.body ?? '';

        const youtubeIds = ZendeskArticleModel.extractYoutubeIds(rawBodyHtml);
        const youtubeEmbedUrls = youtubeIds.map(id => `https://www.youtube.com/embed/${id}?rel=0`)

        let bodyHtml = ZendeskArticleModel.stripYoutubeLinks(rawBodyHtml);

        bodyHtml = ZendeskArticleModel.proxyImageUrls(bodyHtml);


        const searchableArticle: SearchGlobalModel.SearchArticleDocument = {
            type: 'article',
            name: article.title ?? article.name ?? 'untitled article',
            subInfo: category,
            icon: 'help-center',
            _id: article.id.toString(),
            raw: {
                id: article.id,
                title: article.title ?? article.name ?? 'untitled article',
                htmlUrl: article.html_url,
                bodyHtml,
                createdAt: article.created_at,
                updatedAt: article.updated_at,
                category,
                youtubeIds,
                youtubeEmbedUrls
            }
        }

        searchableArticles.push(searchableArticle);
    })

    return {
        searchableArticles
    };

}